const API_BASE_URL = "https://reliablelearning.in/examapi/";

// =============================================
// AUTH APIs
// =============================================

export const loginUser = async (credentials) => {
  const response = await fetch(`${API_BASE_URL}login.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(credentials),
  });
  return response.json();
};

export const registerUser = async (userData) => {
  const response = await fetch(`${API_BASE_URL}register.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(userData),
  });
  return response.json();
};

// ✅ ALIASES for backward compatibility
export const login = loginUser;
export const register = registerUser;

// =============================================
// QUESTION GROUP APIs
// =============================================

export const createGroup = async (group) => {
  const response = await fetch(`${API_BASE_URL}quegrp/create.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(group),
  });
  return response.json();
};

export const getGroups = async () => {
  const response = await fetch(`${API_BASE_URL}quegrp/read.php`, {
    method: "GET",
  });
  return response.json();
};

export const updateGroup = async (group) => {
  const response = await fetch(`${API_BASE_URL}quegrp/update.php`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(group),
  });
  return response.json();
};

export const deleteGroup = async (gid) => {
  const response = await fetch(`${API_BASE_URL}quegrp/delete.php`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ gid }),
  });
  return response.json();
};

// ✅ ALIAS for backward compatibility
export const createQueGrp = createGroup;

// =============================================
// EXAM APIs - ALL FUNCTIONS
// =============================================

// Fetch all exams
export const getExam = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}exam/fetch_all_exams.php`);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error fetching exams:', error);
    throw error;
  }
};

// Get exam by ID
export const getExamById = async (eid) => {
  try {
    const response = await fetch(`${API_BASE_URL}exam/get_exam_by_id.php?eid=${eid}`);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error fetching exam by ID:', error);
    throw error;
  }
};

// Add exam
export const addExam = async (examData) => {
  try {
    const response = await fetch(`${API_BASE_URL}exam/add_exam.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(examData),
    });
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error adding exam:', error);
    throw error;
  }
};

// Update exam
export const updateExamDetails = async (eid, examDetails) => {
  try {
    const response = await fetch(`${API_BASE_URL}exam/update_exam.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eid, ...examDetails }),
    });
    const data = await response.json();
    return { success: true, ...data };
  } catch (error) {
    console.error('Error updating exam:', error);
    return { success: false, message: error.message };
  }
};

// Delete exam
export const deleteExam = async (eid) => {
  try {
    const response = await fetch(`${API_BASE_URL}exam/delete_exam.php`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eid }),
    });
    const data = await response.json();
    return { success: true, ...data };
  } catch (error) {
    console.error('Error deleting exam:', error);
    return { success: false, message: error.message };
  }
};

// ✅ ADD THIS NEW FUNCTION
// ✅ CHECK IF EXAM ALREADY SUBMITTED
export const checkExamSubmission = async (eid, uid) => {
  try {
    console.log('🔍 Checking submission status for:', { eid, uid });
    const response = await fetch(`${API_BASE_URL}exam/check-exam-submission.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ eid, uid })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Submission check result:', data);
    return data;
  } catch (error) {
    console.error('❌ Error checking exam submission:', error);
    return { submitted: false, error: error.message };
  }
};


// ✅ CHECK EXAM AVAILABILITY - UPDATED FOR TIME SLOT VALIDATION
export const checkExamAvailability = async (eid) => {
  try {
    console.log('🔍 Checking availability for exam:', eid);
    const response = await fetch(
      `${API_BASE_URL}exam/check_exam_availability.php?eid=${eid}`
    );
    if (!response.ok) {
      console.error('❌ Response not OK:', response.status);
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    console.log('✅ Availability result:', data);
    return data;
  } catch (error) {
    console.error('❌ Error checking exam availability:', error);
    throw error;
  }
};

// ✅ ALIAS for backward compatibility
export const updateExam = updateExamDetails;

// =============================================
// INSTRUCTION APIs
// =============================================

export const getInst = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}inst/getinst.php`);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error('Error fetching instructions:', error);
    throw error;
  }
};

export const getInsfromExamId = async (eid) => {
  if (!eid) {
    throw new Error("Exam ID is required");
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}exam/getinstfromexamid.php?eid=${eid}`
    );
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching exam instructions:", error);
    throw error;
  }
};

// =============================================
// QUESTION APIs
// =============================================

export const addQuestion = async (questionData) => {
  const response = await fetch(`${API_BASE_URL}question/add_question.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(questionData),
  });
  return response.json();
};

export const fetchQuestions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}question/allquelist.php`);
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching questions:", error);
    throw error;
  }
};

export const fetchQuestionById = async (qid) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}question/getQuestionById.php?qid=${qid}`
    );
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching question:", error);
    throw error;
  }
};

export const updateQuestion = async (qid, payload) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}question/update_question.php?qid=${qid}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error updating question:", error);
    throw error;
  }
};

export const deleteQuestion = async (qid) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}question/delete_question.php?qid=${qid}`,
      {
        method: "DELETE",
      }
    );
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error deleting question:", error);
    throw error;
  }
};

export const getQuestionsByGroupId = async (gid) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}exam/getquegrpbyid.php?gid=${gid}`
    );
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching questions by group:", error);
    throw error;
  }
};

export const getOtherQuestions = async (eid) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}exam/getotherquestions.php?eid=${eid}`
    );
    if (!response.ok) throw new Error("Network response was not ok");
    const data = await response.json();
    if (!Array.isArray(data)) throw new Error("Response is not an array");
    return data;
  } catch (error) {
    console.error("Error fetching other questions:", error);
    throw error;
  }
};

export const updateQuestionEid = async (qid, eid) => {
  try {
    console.log('🔄 updateQuestionEid called with:', { qid, eid });
    const response = await fetch(`${API_BASE_URL}exam/updatequestioneid.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ questionId: qid, examId: eid }),
    });
    if (!response.ok) {
      console.error('❌ Response not OK:', response.status);
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    console.log('✅ updateQuestionEid response:', data);
    return data;
  } catch (error) {
    console.error("❌ Error updating question EID:", error);
    throw error;
  }
};

export const removeQuestion = async (qid) => {
  try {
    console.log('🗑️ removeQuestion called with qid:', qid);
    const response = await fetch(`${API_BASE_URL}exam/removequestion.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ questionId: qid }),
    });
    if (!response.ok) {
      console.error('❌ Response not OK:', response.status);
      throw new Error("Failed to delete question");
    }
    const data = await response.json();
    console.log('✅ removeQuestion response:', data);
    return data;
  } catch (error) {
    console.error("❌ Error removing question:", error);
    throw error;
  }
};

export const getQuestionsDetail = async (eid) => {
  if (!eid) {
    throw new Error("Exam ID is required");
  }
  try {
    console.log('🔍 Fetching questions for exam ID:', eid);
    const response = await fetch(
      `${API_BASE_URL}exam/getquestionsandoptions.php?eid=${eid}`
    );
    if (!response.ok) {
      console.error("❌ Failed to fetch questions, response not ok:", response.status);
      return [];
    }
    const data = await response.json();
    if (data.error) {
      console.error("❌ Error in response:", data.error);
      return [];
    }
    if (!Array.isArray(data)) {
      console.warn("⚠️ Response is not an array, got:", typeof data, data);
      return [];
    }
    console.log(`✅ Fetched ${data.length} questions for exam ${eid}`);
    return data;
  } catch (error) {
    console.error("❌ Error fetching exam questions:", error);
    return [];
  }
};

export const getQuestionsAndOptions = async (eid) => {
  if (!eid) {
    throw new Error("Exam ID is required");
  }

  try {
    console.log('🔍 Fetching questions and options for student exam:', eid);
    
    // ✅ Using the correct endpoint
    const response = await fetch(
      `${API_BASE_URL}exam/getquebyeid.php?eid=${eid}`
    );

    if (!response.ok) {
      console.error("❌ Failed to fetch questions, status:", response.status);
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    
    // Check for errors
    if (data.error) {
      console.error("❌ API Error:", data.error);
      throw new Error(data.error);
    }

    // Validate response
    if (!Array.isArray(data)) {
      console.error("❌ Expected array, got:", typeof data);
      throw new Error("Invalid response format");
    }

    console.log(`✅ Fetched ${data.length} questions for student exam ${eid}`);
    
    if (data.length > 0) {
      console.log('📊 First question:', data[0]);
      console.log('📊 First question has options:', data[0]?.options?.length || 0);
    }
    
    return data;
  } catch (error) {
    console.error("❌ Error fetching exam questions:", error);
    throw error;
  }
};


export const getQuestionsByGroupIdOld = async (gid) => {
  const response = await fetch(`${API_BASE_URL}getQuestions.php?gid=${gid}`);
  return response.json();
};

// =============================================
// TEST APIs
// =============================================

export const saveQuestion = async (questionData) => {
  const response = await fetch(`${API_BASE_URL}test/save_question.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(questionData),
  });
  return response.json();
};

export const fetchSavedData = async () => {
  const response = await fetch(`${API_BASE_URL}test/fetch_saved_data.php`, {
    method: "GET",
  });
  return response.json();
};

export const fetchQuestionsWithOptions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}techexam/getQuestionsWithOptions.php`);
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching questions and options:", error);
    throw error;
  }
};

// =============================================
// ANSWER SUBMISSION APIs
// =============================================

export const submitAnswer = async (answerData) => {
  const response = await fetch(`${API_BASE_URL}submitAnswer.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(answerData),
  });
  return response.json();
};

// ✅ SAVE EXAM ANSWERS
export const saveAnswer = async (answersData) => {
  try {
    console.log('📤 Sending answers to API:', answersData);
    
    const response = await fetch(`${API_BASE_URL}exam/submitans.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(answersData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Server response:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error saving answers:', error);
    throw error;
  }
};


// =============================================
// STATUS APIs
// =============================================

export const updateStatus = async (statusData) => {
  const response = await fetch(`${API_BASE_URL}updateStatus.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(statusData),
  });
  return response.json();
};

export const getStatus = async (examId, userId) => {
  const response = await fetch(
    `${API_BASE_URL}getStatus.php?exam_id=${examId}&user_id=${userId}`
  );
  return response.json();
};

// =============================================
// USER APIs
// =============================================

export const getUsers = async () => {
  const response = await fetch(`${API_BASE_URL}user/fetch_users.php`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return response.json();
};

export const getUsersByDate = async (startDate, endDate) => {
  const response = await fetch(
    `${API_BASE_URL}user/fetch_users_by_date.php?start_date=${startDate}&end_date=${endDate}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );
  return response.json();
};

export const getUserById = async (userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}user/getUserById.php?uid=${userId}`
    );
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

// ✅ ALIASES for backward compatibility
export const fetchUsers = getUsers;
export const fetchUsersByDate = getUsersByDate;

// =============================================
// RESULTS APIs
// =============================================

export const fetchUserResults = async (uid) => {
  try {
    const url = uid
      ? `${API_BASE_URL}results/getUserResults.php?uid=${uid}`
      : `${API_BASE_URL}results/getUserResults.php`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching results:", error);
    throw error;
  }
};

// Check username availability
export const checkUsernameAvailability = async (username) => {
  try {
    const response = await fetch(`${API_BASE_URL}/check_username.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    return await response.json();
  } catch (error) {
    console.error('Error checking username:', error);
    throw error;
  }
};


export const fetchDetailedResults = async (uid) => {
  const response = await fetch(
    `${API_BASE_URL}results/singlereult.php?uid=${uid}`
  );
  if (!response.ok) {
    throw new Error("Failed to fetch detailed results");
  }
  return await response.json();
};

export const getReport = async (examId, userId) => {
  const response = await fetch(
    `${API_BASE_URL}getReport.php?exam_id=${examId}&user_id=${userId}`
  );
  return response.json();
};

// ✅ ALIASES for backward compatibility
export const getResults = fetchUserResults;
export const getSingleResult = fetchDetailedResults;

// Export API_BASE_URL for use in other modules if needed
export { API_BASE_URL };
